import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    HostListener,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { DrawerService, IDrawerPanel } from '../../../services/drawer.service';
import { animate, style, transition, trigger } from '@angular/animations';

interface IRenderedDrawerPanel extends IDrawerPanel {
    ref: ComponentRef<unknown>;
}

@Component({
    selector: 'app-drawer-container',
    templateUrl: './drawer-container.component.html',
    styleUrls: ['./drawer-container.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('150ms ease', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('150ms ease', style({ opacity: 0 })),
            ]),
        ]),
        trigger('slideInAnimationRight', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('150ms ease-in-out', style({ transform: 'translateX(0)' })),
            ]),
            transition(':leave', [
                animate('150ms ease-in-out', style({ transform: 'translateX(100%)' })),
            ]),
        ]),
        trigger('slideInAnimationLeft', [
            transition(':enter', [
                style({ transform: 'translateX(-100%)' }),
                animate('150ms ease-in-out', style({ transform: 'translateX(0)' })),
            ]),
            transition(':leave', [
                animate('150ms ease-in-out', style({ transform: 'translateX(-100%)' })),
            ]),
        ]),
    ],
    standalone: true,
})
export class DrawerContainerComponent implements OnInit, OnDestroy {

    private _item: IRenderedDrawerPanel = null;

    public open = signal<boolean>(false);

    // ----------[ Refs ]----------

    @ViewChild('container', { read: ViewContainerRef, static: false }) containerRef: ViewContainerRef;

    constructor(
        private drawerService: DrawerService,
        private changeDetector: ChangeDetectorRef,
    ) {
    }

    // ----------[ Lifecycle Hooks ]----------

    ngOnInit() {
        this.drawerService.set.subscribe(this.onSet.bind(this));
        this.drawerService.unset.subscribe(this.onUnset.bind(this));
    }

    ngOnDestroy() {
        this.drawerService.set.unsubscribe();
        this.drawerService.unset.unsubscribe();
    }

    // ----------[ Event Handlers ]----------

    onSet(item: IDrawerPanel) {
        this._item = {
            ...item,
            ref: null,
        };

        this.open.set(true);

        this.changeDetector.detectChanges();

        const ref = this.createComponent(item);

        if (item.options.onInit) {
            item.options.onInit({
                component: ref.instance,
                close: () => this.close(),
            });
        }

        this._item.ref = ref;
    }

    onUnset() {
        this.destroyComponent();
        this._item = null;
        this.open.set(false);
    }

    // ----------[ Public Methods ]----------

    close() {
        this.drawerService.close();
    }

    /**
     * The backdrop is marked presentational — it is not a control and cannot be
     * tabbed to — so Escape is the keyboard's only way out of an open drawer.
     */
    @HostListener('document:keydown.escape')
    closeOnEscape() {
        if (this.open()) {
            this.close();
        }
    }

    // ----------[ Private Methods ]----------

    private createComponent(item: IDrawerPanel): ComponentRef<unknown> {
        return this.containerRef.createComponent(item.component);
    }

    private destroyComponent() {
        this._item.ref.destroy();
    }

    // ----------[ Getters ]----------

    get item(): IRenderedDrawerPanel {
        return this._item;
    }

}
